/**
 * Learner micro-frontend: routing, theme, and auth gate.
 */
import React, { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAsterController } from "context";
import Page404 from "pages/Page404";
import MainLayout from "layouts/MainLayout";
import Header from "layouts/Header";
import Dashboard from "pages/dashboard";
import Assigned from "pages/assigned";
import Explore from "pages/explore";
import Chat from "pages/chat";
import Network from "pages/network";
import Analysis from "pages/analysis";
import Profile from "pages/profile";
import Settings from "pages/settings";
import Guide from "pages/guide";
import Board from "pages/board";
import Course from "pages/course";
import AuthProtector from "AuthProtector";
import lightTheme from "assets/theme";
import darkTheme from "assets/theme-dark";
import Footer from "layouts/Footer";
import BoardPreviewGate from "pages/board/board.preview.gate";
import BoardPreview from "pages/board/board.preview";
import LearningMfeRedirect from "pages/board/learningMfeRedirect";
import PageFixer from "pages/PageFixer";
import OfflineBanner from "offline/OfflineBanner";
import { HomeRedirect, LocationKeeper } from "offline/LocationKeeper";

/**
 * Root router for the learner app. Applies light/dark theme and protected routes.
 */
const App = () => {
  const [controller] = useAsterController();
  const { darkMode, themes } = controller;

  useEffect(() => {
    if ((themes || []).length) {
      const raw = (themes || []).find((v) => v.name === "color_main3")?.value || "#F76060";
      const color = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(raw) ? raw : "#F76060";

      document.getElementById("custom-style").innerText = `
        .v-tabs .MuiTabs-indicator { background-color: ${color} !important; }
      `;
    }
  }, [themes.length, themes]);

  return (
    <>
      <ThemeProvider theme={darkMode ? darkTheme(themes) : lightTheme(themes)}>
        <CssBaseline />
        <ToastContainer position="bottom-right" autoClose={7000} hideProgressBar newestOnTop={false} pauseOnHover closeOnClick theme={darkMode ? "dark" : "light"} style={{ width: "350px" }} />
        <PageFixer />
        <OfflineBanner />
        <LocationKeeper />
        <AuthProtector>
          <Box className="min-h-screen flex flex-col">
            <Header />
            <MainLayout>
              <Routes>
                <Route path="*" element={<Page404 />} />
                <Route path="/" element={<HomeRedirect />} />
                <Route path="index.html" element={<HomeRedirect />} />
                <Route path="page404" element={<Page404 />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="guide/:courseId" element={<Guide />} />
                <Route path="assigned" element={<Assigned />} />
                <Route path="course/:courseId" element={<Course />} />
                {/* Learning MFE / Studio jump_to shapes → Ampela preview */}
                <Route path="course/:courseId/:sequenceId/:verticalId" element={<LearningMfeRedirect />} />
                <Route path="course/:courseId/:sequenceId/:verticalId/*" element={<LearningMfeRedirect />} />
                <Route path="preview/course/:courseId/*" element={<LearningMfeRedirect />} />
                <Route path="content/:courseId/:sectionIndex/:sequenceIndex/:verticalIndex" element={<Board />} />
                <Route path="explore" element={<Explore />} />
                <Route path="profile" element={<Profile />} />
                <Route path="inbox" element={<Chat />} />
                <Route path="network" element={<Network />} />
                <Route path="analysis/:courseId" element={<Analysis />} />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/document" element={<Settings />} />

                <Route path="preview/:courseId" element={<BoardPreviewGate />} />
                <Route path="preview/:courseId/:blockId" element={<BoardPreviewGate />} />
                <Route path="preview/:courseId/:sectionIndex/:sequenceIndex/:verticalIndex" element={<BoardPreview />} />
              </Routes>
            </MainLayout>
            <Footer />
          </Box>
        </AuthProtector>
      </ThemeProvider>
    </>
  );
};

export default App;
